import { useQuery } from '@tanstack/react-query';
import { useObjectiveName } from '../objectives';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { api } from '../api/client';
import type { EngineSummary, Signal } from '../types';
import AlertFeed, { routeToPath } from '../components/engine/AlertFeed';
import ExplainerToggle from '../components/engine/ExplainerToggle';
import Code from '../components/Code';
import NorthstarApexCard from '../components/evolution/NorthstarApexCard';
import { C2_EVOLUTION_ENABLED } from '../evolutionFlag';

// Per-engine visual identity + route. Keyed by the V6 engine ids (1-7).
const ENGINE_VISUAL: Record<number, { icon: string; color: string }> = {
  1: { icon: '⚗', color: 'var(--amber)' },   // Innovation
  2: { icon: '⛓', color: 'var(--gold)' },    // Sourcing
  3: { icon: '⚙', color: 'var(--teal)' },    // Manufacturing
  4: { icon: '⇄', color: 'var(--blue)' },    // Distribution
  5: { icon: '✦', color: 'var(--blossom)' }, // Go-to-Market
  6: { icon: '⚔', color: 'var(--red)' },     // Simulation
  7: { icon: '⌬', color: 'var(--olive)' },   // Corp Dev
};

function moatCounts(e: EngineSummary) {
  let core = 0;
  let commodity = 0;
  for (const i of e.interventions) {
    if (i.moat === 'core') core += 1;
    else commodity += 1;
  }
  return { core, commodity };
}

export default function EngineRoom() {
  const engines = useQuery({ queryKey: ['engines'], queryFn: api.engines });
  const spine = useQuery({ queryKey: ['spine'], queryFn: api.spine });
  const signals = useQuery({ queryKey: ['signals'], queryFn: api.signals });
  const learning = useQuery({ queryKey: ['learning'], queryFn: api.learning });
  const driftList = useQuery({ queryKey: ['drift', 'list'], queryFn: api.driftList });
  const objName = useObjectiveName();

  const spineEngines = useMemo<EngineSummary[]>(
    () => (engines.data ?? []).filter((e) => e.row === 'spine').sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [engines.data],
  );
  const crossEngines = useMemo<EngineSummary[]>(
    () => (engines.data ?? []).filter((e) => e.row !== 'spine'),
    [engines.data],
  );

  const breached = useMemo(() => (signals.data ?? []).filter((s) => s.tripwire_breached), [signals.data]);
  const openDrift = driftList.data ?? [];

  return (
    <div>
      {/* Explainer toggle (default off) — the one sanctioned explanation affordance */}
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
        <ExplainerToggle view="engine-room" />
      </div>

      {/* Alert Feed — plain-language, ranked, clickable (§12.1) */}
      {C2_EVOLUTION_ENABLED && <NorthstarApexCard />}
      <AlertFeed />

      {/* Foundation status layer — sits above the value chain, not an engine tile */}
      <div
        className="panel"
        style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', padding: '12px 18px' }}
      >
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <span className="dot" style={{ background: 'var(--azura)' }} />
          <Link to="/spine" style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink)', textDecoration: 'none' }}>
            Foundation
          </Link>
        </div>
        <FoundationStat to="/spine" value={String(spine.data?.objectives.length ?? '—')} label="objectives" />
        <FoundationStat to="/signals" value={String(signals.data?.length ?? '—')} label="signals" accent={breached.length > 0} />
        <FoundationStat to="/drift" value={String(openDrift.length)} label="open drift" accent={openDrift.length > 0} />
        <FoundationStat to="/scoring" value={String((learning.data?.decisions ?? []).length)} label="decisions logged" />
        <FoundationStat to="/learning" value={String((learning.data?.weight_history ?? []).length)} label="weight nudges" />
      </div>

      {/* Open drift packets — real state, no caption */}
      {openDrift.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: openDrift.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(360px, 1fr))', gap: 10 }}>
            {openDrift.map((p) => (
              <Link key={p.id} to={`/drift/${p.id}`} style={{ textDecoration: 'none' }}>
                <div className="driftcard">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <div className="row">
                      <span className="tag red">DRIFT</span>
                      <b className="cond" style={{ fontSize: 16, letterSpacing: '.02em' }}>{p.signal_name}</b>
                    </div>
                    <span className="small">open →</span>
                  </div>
                  <div className="small" style={{ marginTop: 6 }}>
                    Breaches <b style={{ color: 'var(--red)' }}><Code id={p.assumption_id} kind="assumption" compact /></b> · threatens{' '}
                    {objName(p.objective_id) && <span className="tag amber">{objName(p.objective_id)}</span>} · owner {p.owner}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* The CPG value chain — five spine engines, left to right, with flow */}
      <div className="cv-flow">
        {spineEngines.map((e, i) => (
          <div key={e.id} className="cv-flow-item">
            <EngineTile engine={e} />
            {i < spineEngines.length - 1 && <span className="cv-arrow" aria-hidden>→</span>}
          </div>
        ))}
      </div>

      {/* Non-sequential engines — run across the spine */}
      <div className="cv-cross">
        {crossEngines.map((e) => (
          <EngineTile key={e.id} engine={e} cross />
        ))}
      </div>

      {/* Signal registry — split External (what the market is doing) vs Internal (our operation) */}
      <div className="panel" style={{ marginTop: 18 }}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span className="dot" style={{ background: 'var(--amber)' }} />
            <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Signal registry</span>
          </div>
          <span className="small faint">{signals.data?.length ?? 0} watched · {breached.length} breached</span>
        </div>
        <SignalGroup title="External · what the market is doing" signals={(signals.data ?? []).filter((s) => (s.class ?? 'external') === 'external')} />
        <div style={{ height: 14 }} />
        <SignalGroup title="Internal · what our operation is doing" signals={(signals.data ?? []).filter((s) => s.class === 'internal')} />
      </div>
    </div>
  );
}

function SignalGroup({ title, signals }: { title: string; signals: Signal[] }) {
  const objName = useObjectiveName();
  if (signals.length === 0) return null;
  return (
    <div>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{title}</div>
      <div className="signal-registry-grid">
        {signals.map((s) => {
          const cls = s.tripwire_breached ? 'red' : s.current_value > 0.4 ? 'amber' : 'muted';
          const to = C2_EVOLUTION_ENABLED && s.id === 'S-09' ? '/cascade' : s.route ? routeToPath(s.route) : `/engine/1`;
          return (
            <Link key={s.id} to={to} style={{ textDecoration: 'none' }}>
              <div
                className={`signal-registry-card${s.tripwire_breached ? ' breached' : ''}`}
                style={{ borderLeft: s.tripwire_breached ? '4px solid var(--red)' : '1px solid var(--line)' }}
              >
                <div className="signal-registry-meta">
                  <span className={`signal-registry-id ${s.tripwire_breached ? 'sev-chip high' : `tag ${cls}`}`}>
                    <Code id={s.id} kind="signal" compact codeOnly />
                    {s.tripwire_breached ? ' · BREACHED' : ''}
                  </span>
                  <span className="signal-registry-node tiny faint">
                    <Code id={s.money_node_id} kind="stage" compact truncate />
                  </span>
                </div>
                <div className="signal-registry-title">{s.name}</div>
                {objName(s.objective_id) && (
                  <div className="signal-registry-obj" title={objName(s.objective_id)}>{objName(s.objective_id)}</div>
                )}
                <div className="signal-registry-footer">
                  <span className="signal-registry-source small faint">{s.source_ref ?? s.owner}</span>
                  <span className="small" style={{ color: 'var(--azura)', fontSize: 11 }}>
                    {C2_EVOLUTION_ENABLED && s.id === 'S-09' ? 'cascade ->' : 'open ->'}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function FoundationStat({ to, value, label, accent }: { to: string; value: string; label: string; accent?: boolean }) {
  return (
    <Link to={to} style={{ textDecoration: 'none' }}>
      <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
        <span className="mono" style={{ fontSize: 18, fontWeight: 700, color: accent ? 'var(--gold)' : 'var(--ink)' }}>{value}</span>
        <span className="small faint">{label}</span>
      </div>
    </Link>
  );
}

const CRIT = {
  action: { color: 'var(--red)', label: 'ACTION' },
  watch: { color: 'var(--gold)', label: 'WATCH' },
  ok: { color: 'var(--olive)', label: 'OK' },
} as const;
const TREND_ICON = { up: '▲', down: '▼', flat: '▬' } as const;

function EngineTile({ engine, cross }: { engine: EngineSummary; cross?: boolean }) {
  const visual = ENGINE_VISUAL[engine.id] ?? { icon: '◆', color: 'var(--muted)' };
  const tile = engine.tile;
  const kpi = tile?.headline_kpi;
  const critKey = tile?.criticality ?? 'ok';
  const crit = CRIT[critKey];
  const { core, commodity } = moatCounts(engine);
  const subTabCount = engine.interventions.filter((i) => i.ui_kind === 'sub_tab').length;
  const shortName = engine.name.replace(/\s*\(.*\)\s*/, '');
  // vs-target color: under target on a KPI → caution
  const vsColor = kpi?.vs_target === 'under' ? 'var(--gold)' : kpi?.vs_target === 'over' ? 'var(--olive)' : 'var(--muted)';
  const tileClass = critKey === 'action' ? 'action-tile' : critKey === 'watch' ? 'watch-tile' : '';

  return (
    <Link to={`/engine/${engine.id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <div
        className={`panel cv-tile ${tileClass}`}
        style={{ height: '100%', borderLeft: `5px solid ${crit.color}`, borderTop: `1px solid var(--line)`, cursor: 'pointer' }}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div className="row" style={{ gap: 10 }}>
            <span style={{ fontSize: 19, lineHeight: 1, color: 'var(--faint)', width: 22, textAlign: 'center', flexShrink: 0 }}>{visual.icon}</span>
            <div>
              <div className="small faint" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{cross ? 'Cross-cutting' : 'Operating stage'}</div>
              <div className="cond" style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.15 }}>{shortName}</div>
            </div>
          </div>
          <span className={`crit-chip ${critKey}`}>
            {critKey === 'action' && <span className="flash-dot" />}
            {crit.label}
          </span>
        </div>

        {kpi && (
          <div style={{ marginBottom: 8 }}>
            <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
              <div className="num" style={{ fontSize: 22, color: 'var(--ink)' }}>{kpi.value}</div>
              <span title={`trend ${kpi.trend}`} style={{ fontSize: 12, color: kpi.trend === 'up' ? 'var(--olive)' : kpi.trend === 'down' ? 'var(--red)' : 'var(--muted)' }}>{TREND_ICON[kpi.trend]}</span>
              <span className="mono" style={{ fontSize: 10.5, color: vsColor, textTransform: 'uppercase' }}>{kpi.vs_target} target</span>
            </div>
            <div className="small faint">{kpi.label}</div>
          </div>
        )}

        {tile?.headline_plain && (
          <div className="small" style={{ color: 'var(--muted)', lineHeight: 1.4, marginBottom: 8, minHeight: 34 }}>{tile.headline_plain}</div>
        )}

        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="small faint" style={{ fontSize: 11.5 }}>{engine.owner}</span>
          <div className="row" style={{ gap: 6 }}>
            <span className="small mono" style={{ fontSize: 12, color: 'var(--ink)', fontWeight: 700 }}>{subTabCount} plays</span>
            <span title={`${core} Core · ${commodity} Commodity`} className="mono" style={{ fontSize: 12, color: 'var(--amber)', fontWeight: 700 }}>
              ◆{core}{commodity > 0 && <span style={{ color: 'var(--ink)' }}> ○{commodity}</span>}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
